import { FIELD_GROUPS, PROFILE_FIELDS, formatHeight, formatPhone } from '@accheyrishtey/shared';
import { formatDate } from './format';

interface SharePhoto {
  url: string;
}

export interface ShareProfile extends Record<string, unknown> {
  full_name: string;
  age?: number | null;
  photos?: SharePhoto[];
}

export interface WhatsAppTextOptions {
  includePhotos?: boolean;
}

function isProvided(value: unknown): boolean {
  return value !== null
    && value !== undefined
    && (typeof value !== 'string' || value.trim() !== '');
}

function line(label: string, value: unknown): string | null {
  if (!isProvided(value)) return null;
  return `*${label}:* ${String(value).trim()}`;
}

function formattedFieldValue(profile: ShareProfile, key: string): string | number | null {
  const value = profile[key];
  if (!isProvided(value)) return null;
  if (key === 'height_cm' || key === 'pref_height_cm') {
    return formatHeight(Number(value)) || null;
  }
  if (key.includes('contact_number')) {
    return formatPhone(String(value)) || null;
  }
  if (key === 'date_of_birth') return formatDate(String(value)) || null;
  return typeof value === 'number' ? value : String(value).trim();
}

/**
 * Builds share text from the same metadata that drives the intake and detail
 * screens. Database metadata, stages, stars, consent, and private notes are
 * intentionally excluded.
 */
export function buildWhatsAppText(
  profile: ShareProfile,
  options: WhatsAppTextOptions = {},
): string {
  const sections = FIELD_GROUPS.flatMap((group) => {
    const fieldLines = PROFILE_FIELDS
      .filter((field) => field.group === group.key)
      .flatMap((field) => {
        const valueLine = line(field.label, formattedFieldValue(profile, field.key));
        const lines = valueLine ? [valueLine] : [];
        if (field.key === 'date_of_birth') {
          const ageLine = line('Age', profile.age);
          if (ageLine) lines.push(ageLine);
        }
        return lines;
      });

    return fieldLines.length > 0 ? [`_${group.label}_`, ...fieldLines, ''] : [];
  });

  const photoLines = options.includePhotos
    ? (profile.photos ?? [])
      .map((photo) => photo.url?.trim())
      .filter((url): url is string => Boolean(url))
      .map((url, index) => `${index + 1}. ${url}`)
    : [];

  const message = [
    '*Acchey Rishtey*',
    `*${profile.full_name.trim()}*`,
    '',
    ...sections,
    ...(photoLines.length > 0 ? ['_Photographs_', ...photoLines] : []),
  ];

  while (message.at(-1) === '') message.pop();
  return message.join('\n');
}

export function whatsAppShareUrl(
  profile: ShareProfile,
  options: WhatsAppTextOptions = {},
): string {
  return `https://wa.me/?text=${encodeURIComponent(buildWhatsAppText(profile, options))}`;
}
